/**
 * BillingSettingsPage
 *
 * Billing management page for customer admins and super admins.
 *
 * Customer admins see:
 *   - Current billing plan
 *   - Stored payment methods (add/remove)
 *   - Billing history
 *
 * Super admins additionally see:
 *   - Billing strategy selector
 *   - Pricing override editor
 */

import React, { useState, useContext, useCallback, useEffect, Fragment } from 'react';
import {
    Button, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, Chip, Tooltip, Select,
    MenuItem, FormControl, InputLabel, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Alert,
    Skeleton, Divider, Card, CardContent, CardActions, FormControlLabel, Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

import { PageHeader } from '../design-system';
import styles from './page-manage-billing-settings.module.css';

import UserProfileContext from '../context/userprofilecontext.js';
import SnackbarContext from '../context/snackbar-context.js';
import {
    useGetBillingSettings,
    deletePaymentMethod,
    useGetAvailableStrategies,
    updateBillingStrategy,
    upsertBillingOverride,
    updateCustomerDetails,
    deleteBillingOverride,
    useGetCaseUsageSummary,
    updatePaymentMethod,
    setDefaultPaymentMethod,
    setCasePaymentMethod,
    adminFinalizeInvoice,
    adminMidperiodCharge,
} from '../api/api-billing-settings.js';
import IntuitPaymentCapture from '../components/ui/IntuitPaymentCapture.js';
import PricingDisclosure from '../components/ui/PricingDisclosure.js';
import { storePaymentMethod } from '../api/api-billing-settings.js';
import { useConfirmAction } from '../components/ui/confirmactionhandler.js';
import { formatCurrency } from '../utils/formatters';
import { mutate } from 'swr';


// ──────────────────────────────────────────────────────────────────────
// Strategy display names
// ──────────────────────────────────────────────────────────────────────

const STRATEGY_LABELS = {
    paygo: { label: 'Pay As You Go', description: 'Usage-based billing with per-event charges' },
    prepaid_standard: { label: 'Prepaid Standard', description: 'Quota-based with prepaid allowances' },
    flat_unlimited: { label: 'Flat Unlimited', description: 'Fixed monthly fee with unlimited usage' },
};

function getStrategyLabel(strategy) {
    return STRATEGY_LABELS[strategy]?.label || strategy;
}

function getStrategyDescription(strategy) {
    return STRATEGY_LABELS[strategy]?.description || '';
}


// ──────────────────────────────────────────────────────────────────────
// Period status config
// ──────────────────────────────────────────────────────────────────────

const PERIOD_STATUS = {
    OPEN: { label: 'Open', color: 'info' },
    FINALIZED: { label: 'Finalized', color: 'success' },
};

// Usage line-item status by source. `accrued` = recorded in the BillingEvent
// ledger (committed — bills at finalize regardless); `projected` = an estimate
// (remaining recurring days / projected AI usage) that depends on the rest of
// the period; `invoice` = already on the QBO invoice.
const LINE_ITEM_STATUS = {
    invoice: { label: 'Billed', color: 'success' },
    accrued: { label: 'Accrued', color: 'info' },
    projected: { label: 'Projected', color: 'warning' },
};


// ──────────────────────────────────────────────────────────────────────
// Payment Methods Section
// ──────────────────────────────────────────────────────────────────────

const TERMS_URL = 'https://livisibly.com/terms';

/** "Visa ending in 4242" / "Checking ending in 1234" — shared by the card and the per-case picker. */
function formatMethodLabel(method) {
    if (!method) return '';
    return method.type === 'card'
        ? `${method.cardType || 'Card'} ending in ${method.last4}`
        : `${method.accountType || 'Account'} ending in ${method.last4}`;
}

function PaymentMethodCard({ method, onDelete, onUpdate, onSetDefault, isDeleting, isUpdating, isSettingDefault }) {
    const isCard = method.type === 'card';

    return (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                {isCard ? (
                    <CreditCardIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                ) : (
                    <AccountBalanceIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                )}
                <div className={styles.paymentCardInfo}>
                    <p className={styles.paymentCardTitle}>
                        {formatMethodLabel(method)}
                    </p>
                    <p className={styles.paymentCardSub}>
                        {isCard && method.expMonth && method.expYear
                            ? `Expires ${method.expMonth}/${method.expYear}`
                            : method.name || ''
                        }
                    </p>
                </div>
                {method.isDefault ? (
                    <Chip label="Default" size="small" color="primary" variant="outlined" />
                ) : (
                    <Tooltip title="Set as default payment method">
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onSetDefault(method)}
                            disabled={isSettingDefault}
                            sx={{ fontSize: '0.7rem', px: 1 }}
                        >
                            Set Default
                        </Button>
                    </Tooltip>
                )}
                <Tooltip title="Update payment details">
                    <IconButton
                        size="small"
                        onClick={() => onUpdate(method)}
                        disabled={isUpdating}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Remove payment method">
                    <IconButton
                        size="small"
                        onClick={() => onDelete(method)}
                        disabled={isDeleting}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </CardContent>
        </Card>
    );
}

function PaymentMethodsSection({ paymentMethods, customerID, onRefresh }) {
    const snackbarContext = useContext(SnackbarContext);
    const [showAddForm, setShowAddForm] = useState(false);
    const [updatingMethod, setUpdatingMethod] = useState(null); // method being updated
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSettingDefault, setIsSettingDefault] = useState(false);
    const [setAsDefault, setSetAsDefault] = useState(false);

    const handleTokenReceived = useCallback(async (token, tokenType, recaptchaToken, billingAddress) => {
        setIsSubmitting(true);
        try {
            if (updatingMethod) {
                await updatePaymentMethod({
                    customerID,
                    oldMethodId: updatingMethod.id,
                    oldMethodType: updatingMethod.type,
                    paymentToken: token,
                    tokenType,
                    recaptchaToken,
                    billingAddress,
                    setAsDefault,
                });
                snackbarContext.openSnackbarSuccess('Payment method updated successfully');
                setUpdatingMethod(null);
            } else {
                await storePaymentMethod({
                    customerID,
                    paymentToken: token,
                    tokenType,
                    recaptchaToken,
                    billingAddress,
                    setAsDefault,
                });
                snackbarContext.openSnackbarSuccess('Payment method added successfully');
            }
            setShowAddForm(false);
            setSetAsDefault(false);
            onRefresh();
        } catch (error) {
            console.error('Error storing payment method:', error);
            let msg = 'Failed to save payment method';
            try {
                const resp = error._response || error.response;
                if (resp?.body) {
                    let body = resp.body;
                    if (typeof body === 'string') {
                        // Handle Python-style single quotes from format_error_response
                        body = body.replace(/'/g, '"');
                        // Strip outer quotes if double-stringified
                        if (body.startsWith('"') && body.endsWith('"')) {
                            body = body.slice(1, -1);
                        }
                        const parsed = JSON.parse(body);
                        msg = parsed.message || parsed.error || msg;
                    }
                }
            } catch (parseErr) {
                console.error('Error parsing response:', parseErr);
            }
            snackbarContext.openSnackbarError(msg);
        } finally {
            setIsSubmitting(false);
        }
    }, [customerID, updatingMethod, setAsDefault, snackbarContext, onRefresh]);

    const handleUpdate = useCallback((method) => {
        setUpdatingMethod(method);
        setShowAddForm(true);
    }, []);



    const handleSetDefault = useCallback(async (method) => {
        setIsSettingDefault(true);
        try {
            await setDefaultPaymentMethod({
                customerID,
                methodId: method.id,
                methodType: method.type,
            });
            snackbarContext.openSnackbarSuccess('Default payment method updated');
            onRefresh();
        } catch (error) {
            console.error('Error setting default:', error);
            snackbarContext.openSnackbarError('Failed to set default payment method');
        } finally {
            setIsSettingDefault(false);
        }
    }, [customerID, snackbarContext, onRefresh]);

    const handleDelete = useCallback(async (method) => {
        setIsDeleting(true);
        try {
            await deletePaymentMethod({
                customerID,
                methodId: method.id,
                methodType: method.type,
            });
            snackbarContext.openSnackbarSuccess('Payment method removed');
            onRefresh();
        } catch (error) {
            console.error('Error deleting payment method:', error);
            snackbarContext.openSnackbarError('Failed to remove payment method');
        } finally {
            setIsDeleting(false);
        }
    }, [customerID, snackbarContext, onRefresh]);

    const { handleConfirmAction: handleConfirmDelete, ConfirmDialogElement: ConfirmDeleteDialog } =
        useConfirmAction(handleDelete, {
            title: 'Remove Payment Method',
            confirmMessage: 'Are you sure you want to remove this payment method?',
        });

    const handleCancelForm = () => {
        setShowAddForm(false);
        setUpdatingMethod(null);
        setSetAsDefault(false);
    };

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                    Payment Methods
                </h3>
                {!showAddForm && (
                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => { setUpdatingMethod(null); setShowAddForm(true); }}
                    >
                        Add Payment Method
                    </Button>
                )}
            </div>

            {paymentMethods.length === 0 && !showAddForm && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No payment methods on file. Add one to continue using the platform.
                </Alert>
            )}

            {paymentMethods.map((method) => (
                <PaymentMethodCard
                    key={method.id}
                    method={method}
                    onDelete={handleConfirmDelete}
                    onUpdate={handleUpdate}
                    onSetDefault={handleSetDefault}
                    isDeleting={isDeleting}
                    isUpdating={isSubmitting}
                    isSettingDefault={isSettingDefault}
                />
            ))}

            {showAddForm && (
                <div className={styles.paymentFormArea}>
                    {updatingMethod && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Updating {updatingMethod.type === 'card'
                                ? `card ending in ${updatingMethod.last4}`
                                : `account ending in ${updatingMethod.last4}`
                            }. Enter the new payment details below.
                        </Alert>
                    )}
                    {paymentMethods.length === 0 && !updatingMethod && (
                        <PricingDisclosure customerID={customerID} />
                    )}
                    <IntuitPaymentCapture
                        onTokenReceived={handleTokenReceived}
                        isSubmitting={isSubmitting}
                    />
                    <div className={styles.paymentFormActions}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={setAsDefault}
                                    onChange={(e) => setSetAsDefault(e.target.checked)}
                                    disabled={isSubmitting}
                                    size="small"
                                />
                            }
                            label={<span className={styles.fieldValue}>Set as default payment method</span>}
                        />
                    </div>
                    <p className={styles.formNote}>
                        Adding a payment method provided by a client or other third party
                        does not change your responsibility for payment.
                    </p>
                    <Button
                        sx={{ mt: 1 }}
                        onClick={handleCancelForm}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                </div>
            )}

            <ConfirmDeleteDialog />
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Per-case Payment Method Section
// ──────────────────────────────────────────────────────────────────────

const USE_CUSTOMER_DEFAULT = '';

function CasePaymentMethodSection({ caseID, caseName, customerID, paymentMethods, casePaymentMethod, onRefresh }) {
    const snackbarContext = useContext(SnackbarContext);
    const [isSaving, setIsSaving] = useState(false);

    const customerDefault = paymentMethods.find((m) => m.isDefault);
    const isStale = Boolean(casePaymentMethod?.stale);
    // The value the server currently reports for this case.
    const serverSelectedId = !isStale && casePaymentMethod?.methodId
        ? String(casePaymentMethod.methodId)
        : USE_CUSTOMER_DEFAULT;

    // Optimistic local mirror: the dropdown reflects the user's choice
    // immediately, without waiting for the refetch that follows the save
    // (~3s round trip). It re-syncs whenever the server value actually
    // changes (successful refetch, or an external update).
    const [selectedId, setSelectedId] = useState(serverSelectedId);
    useEffect(() => {
        setSelectedId(serverSelectedId);
    }, [serverSelectedId]);

    const handleChange = useCallback(async (event) => {
        const value = event.target.value;
        const previous = selectedId;
        setSelectedId(value); // optimistic
        const method = value === USE_CUSTOMER_DEFAULT
            ? null
            : paymentMethods.find((m) => String(m.id) === value);
        setIsSaving(true);
        try {
            await setCasePaymentMethod({
                customerID,
                caseID,
                methodId: method ? method.id : null,
                methodType: method ? method.type : null,
            });
            snackbarContext.openSnackbarSuccess(
                method ? 'Case payment method updated' : 'Case will use the customer default payment method'
            );
            onRefresh();
        } catch (err) {
            setSelectedId(previous); // roll back on failure
            snackbarContext.openSnackbarError('Failed to update the case payment method');
        } finally {
            setIsSaving(false);
        }
    }, [caseID, customerID, paymentMethods, snackbarContext, onRefresh, selectedId]);

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div>
                    <h3 className={styles.sectionTitle}>Payment Method for This Case</h3>
                    <p className={styles.sectionSubtitle}>{caseName || caseID}</p>
                </div>
            </div>

            {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The payment method previously selected for this case is no longer on file.
                    Charges will use the customer default until you choose another method.
                </Alert>
            )}

            <div className={styles.caseMethodRow}>
                <FormControl size="small" fullWidth disabled={isSaving}>
                    <InputLabel id="case-payment-method-label">Charge this case to</InputLabel>
                    <Select
                        labelId="case-payment-method-label"
                        label="Charge this case to"
                        value={selectedId}
                        onChange={handleChange}
                        inputProps={{ 'aria-label': 'Payment method for this case' }}
                        data-testid="case-payment-method-select"
                    >
                        <MenuItem value={USE_CUSTOMER_DEFAULT}>
                            {customerDefault
                                ? `Use customer default (${formatMethodLabel(customerDefault)})`
                                : 'Use customer default (none set)'}
                        </MenuItem>
                        {paymentMethods.map((method) => (
                            <MenuItem key={`${method.type}-${method.id}`} value={String(method.id)}>
                                {formatMethodLabel(method)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <p className={styles.caseMethodNote}>
                    The account holder remains directly responsible for all charges to this case,
                    regardless of which payment method is selected or who provided it. If the selected
                    method is declined, the customer default is charged. See the{' '}
                    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                </p>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Billing Usage Section
// ──────────────────────────────────────────────────────────────────────

function CaseUsageSummary({ caseID, caseName }) {
    const { data: usage, isLoading, error } = useGetCaseUsageSummary(caseID);

    if (isLoading) return <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />;
    if (error) return <Alert severity="error">Failed to load usage summary</Alert>;
    if (!usage) return null;

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div>
                    <h3 className={styles.sectionTitle}>
                        Current Period Usage (Current Case)
                    </h3>
                    <p className={styles.sectionSubtitle}>
                        {caseName || caseID} — {usage.period}
                    </p>
                </div>
                <Chip
                    label={usage.hasOpenInvoice ? 'Invoice Open' : 'No Invoice Yet'}
                    color={usage.hasOpenInvoice ? 'info' : 'default'}
                    size="small"
                    variant="outlined"
                />
            </div>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Unit Price</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="center">Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usage.lineItems.map((item, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{item.description}</TableCell>
                                <TableCell align="right">
                                    {item.quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                                    {item.billingUnit ? ` ${item.billingUnit}` : ''}
                                </TableCell>
                                <TableCell align="right">
                                    ${formatCurrency(item.unitPrice)}
                                    {item.billingUnit ? `/${item.billingUnit}` : ''}
                                </TableCell>
                                <TableCell align="right">${formatCurrency(item.amount)}</TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={LINE_ITEM_STATUS[item.source]?.label || 'Projected'}
                                        size="small"
                                        color={LINE_ITEM_STATUS[item.source]?.color || 'warning'}
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                                Projected Period Total
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                ${formatCurrency(usage.projectedTotal)}
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Billing Strategy Section (super admin only)
// ──────────────────────────────────────────────────────────────────────

function formatStrategyLabel(key) {
    if (!key) return 'Not Assigned';
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatStrategyDescription(strategyData) {
    if (!strategyData) return '';
    const monthly = strategyData.monthlyAmount;
    if (monthly != null) return `$${monthly.toLocaleString()}/case/month`;
    return '';
}

function BillingStrategySection({ currentStrategy, customerID, onRefresh }) {
    const snackbarContext = useContext(SnackbarContext);
    const [editing, setEditing] = useState(false);
    const [selectedStrategy, setSelectedStrategy] = useState(currentStrategy || '');
    const [isSaving, setIsSaving] = useState(false);

    const { data: strategies, isLoading: isLoadingStrategies } = useGetAvailableStrategies(editing);

    const handleSave = useCallback(async () => {
        if (selectedStrategy === currentStrategy) {
            setEditing(false);
            return;
        }
        setIsSaving(true);
        try {
            await updateBillingStrategy({ customerID, billingStrategy: selectedStrategy });
            snackbarContext.openSnackbarSuccess(`Billing strategy updated to ${formatStrategyLabel(selectedStrategy)}`);
            setEditing(false);
            onRefresh();
        } catch (error) {
            console.error('Error updating strategy:', error);
            snackbarContext.openSnackbarError('Failed to update billing strategy');
        } finally {
            setIsSaving(false);
        }
    }, [customerID, selectedStrategy, currentStrategy, snackbarContext, onRefresh]);

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                    Billing Strategy
                </h3>
                <Chip label="Super Admin" size="small" color="error" variant="outlined" />
            </div>

            {!editing ? (
                <div className={styles.strategyRow}>
                    <div>
                        <p className={styles.planName}>
                            {formatStrategyLabel(currentStrategy)}
                        </p>
                    </div>
                    <IconButton size="small" onClick={() => setEditing(true)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </div>
            ) : (
                <div className={styles.strategyRow}>
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel shrink>Strategy</InputLabel>
                        <Select
                            value={selectedStrategy}
                            label="Strategy"
                            onChange={(e) => setSelectedStrategy(e.target.value)}
                            disabled={isSaving || isLoadingStrategies}
                            displayEmpty
                            notched
                        >
                            {!selectedStrategy && (
                                <MenuItem value="" disabled>Select a strategy...</MenuItem>
                            )}
                            {strategies && Object.entries(strategies).map(([key, data]) => (
                                <MenuItem key={key} value={key}>
                                    {formatStrategyLabel(key)} {data.monthlyAmount != null ? `($${data.monthlyAmount}/mo)` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="contained" size="small" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => { setEditing(false); setSelectedStrategy(currentStrategy); }} disabled={isSaving}>
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Pricing Overrides Section (super admin only)
// ──────────────────────────────────────────────────────────────────────

function PricingOverridesSection({ overrides, customerID, onRefresh }) {
    const snackbarContext = useContext(SnackbarContext);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingSku, setEditingSku] = useState(null);
    const [formData, setFormData] = useState({ sku: '', unitPrice: '', scheduledAmount: '', quota_increment: '', costMarkup: '' });
    const [isSaving, setIsSaving] = useState(false);

    const AVAILABLE_SKUS = ['page_upload', 'monthly_service_fee', 'archival_fee', 'reactivation_fee', 'chat_ai_usage'];

    const resetForm = () => {
        setFormData({ sku: '', unitPrice: '', scheduledAmount: '', quota_increment: '', costMarkup: '' });
        setShowAddForm(false);
        setEditingSku(null);
    };

    const handleEdit = (override) => {
        setFormData({
            sku: override.sku,
            unitPrice: override.unitPrice || '',
            scheduledAmount: override.scheduledAmount || '',
            quota_increment: override.quota_increment || '',
            costMarkup: override.costMarkup != null ? override.costMarkup : '',
        });
        setEditingSku(override.sku);
        setShowAddForm(true);
    };

    const handleSave = useCallback(async () => {
        if (!formData.sku) return;
        setIsSaving(true);
        try {
            const payload = { customerID, sku: formData.sku };
            if (formData.unitPrice !== '') payload.unitPrice = parseFloat(formData.unitPrice);
            if (formData.scheduledAmount !== '') payload.scheduledAmount = parseFloat(formData.scheduledAmount);
            if (formData.quota_increment !== '') payload.quota_increment = parseInt(formData.quota_increment);
            if (formData.costMarkup !== '') payload.costMarkup = parseFloat(formData.costMarkup);

            await upsertBillingOverride(payload);
            snackbarContext.openSnackbarSuccess(`Pricing override saved for ${formData.sku}`);
            resetForm();
            onRefresh();
        } catch (error) {
            console.error('Error saving override:', error);
            snackbarContext.openSnackbarError('Failed to save pricing override');
        } finally {
            setIsSaving(false);
        }
    }, [customerID, formData, snackbarContext, onRefresh]);

    const handleDelete = useCallback(async (sku) => {
        try {
            await deleteBillingOverride({ customerID, sku });
            snackbarContext.openSnackbarSuccess(`Pricing override removed for ${sku}`);
            onRefresh();
        } catch (error) {
            console.error('Error deleting override:', error);
            snackbarContext.openSnackbarError('Failed to remove pricing override');
        }
    }, [customerID, snackbarContext, onRefresh]);

    const { handleConfirmAction: handleConfirmDeleteOverride, ConfirmDialogElement: ConfirmDeleteOverrideDialog } =
        useConfirmAction((data) => handleDelete(data.sku), {
            title: 'Remove Pricing Override',
            confirmMessage: 'Remove this pricing override? The customer will revert to the strategy default.',
        });

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div className={styles.sectionTitleIcon}>
                    <TuneIcon color="action" />
                    <h3 className={styles.sectionTitle}>
                        Pricing Overrides
                    </h3>
                </div>
                <div className={styles.sectionTitleIcon}>
                    <Chip label="Super Admin" size="small" color="error" variant="outlined" />
                    {!showAddForm && (
                        <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAddForm(true)}>
                            Add Override
                        </Button>
                    )}
                </div>
            </div>

            {overrides.length === 0 && !showAddForm && (
                <p className={styles.emptyText}>
                    No custom pricing overrides. This customer uses the standard strategy pricing.
                </p>
            )}

            {overrides.length > 0 && (
                <div className={styles.tableArea}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>SKU</TableCell>
                                    <TableCell align="right">Unit Price</TableCell>
                                    <TableCell align="right">Scheduled Amount</TableCell>
                                    <TableCell align="right">Quota Limit</TableCell>
                                    <TableCell align="right">Cost Markup</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {overrides.map((override) => (
                                    <TableRow key={override.sku}>
                                        <TableCell>{override.sku}</TableCell>
                                        <TableCell align="right">
                                            {override.unitPrice != null ? `$${override.unitPrice}` : '\u2014'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {override.scheduledAmount != null ? `$${override.scheduledAmount}` : '\u2014'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {override.quota_increment != null ? override.quota_increment.toLocaleString() : '\u2014'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {override.costMarkup != null ? `${override.costMarkup}×` : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleEdit(override)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleConfirmDeleteOverride(override)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            )}

            {showAddForm && (
                <div className={styles.overrideFormArea}>
                    <p className={styles.overrideFormTitle}>
                        {editingSku ? `Edit Override: ${editingSku}` : 'New Pricing Override'}
                    </p>
                    <FormControl size="small" fullWidth disabled={!!editingSku}>
                        <InputLabel>SKU</InputLabel>
                        <Select
                            value={formData.sku}
                            label="SKU"
                            onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                        >
                            {AVAILABLE_SKUS.map((sku) => (
                                <MenuItem key={sku} value={sku}>{sku}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <div className={styles.overrideFormFields}>
                        <TextField
                            label="Unit Price ($)"
                            size="small"
                            type="number"
                            value={formData.unitPrice}
                            onChange={(e) => setFormData(prev => ({ ...prev, unitPrice: e.target.value }))}
                            inputProps={{ step: "0.001", min: "0" }}
                            fullWidth
                            helperText="Price per unit (e.g., per page)"
                        />
                        <TextField
                            label="Scheduled Amount ($)"
                            size="small"
                            type="number"
                            value={formData.scheduledAmount}
                            onChange={(e) => setFormData(prev => ({ ...prev, scheduledAmount: e.target.value }))}
                            inputProps={{ step: "0.01", min: "0" }}
                            fullWidth
                            helperText="Monthly scheduled fee"
                        />
                        <TextField
                            label="Quota Limit"
                            size="small"
                            type="number"
                            value={formData.quota_increment}
                            onChange={(e) => setFormData(prev => ({ ...prev, quota_increment: e.target.value }))}
                            inputProps={{ step: "1", min: "0" }}
                            fullWidth
                            helperText="Max units before throttle"
                        />
                        <TextField
                            label="Cost Markup (×)"
                            size="small"
                            type="number"
                            value={formData.costMarkup}
                            onChange={(e) => setFormData(prev => ({ ...prev, costMarkup: e.target.value }))}
                            inputProps={{ step: "0.05", min: "0" }}
                            fullWidth
                            helperText="Markup over cost for consumption-priced SKUs (1.0 = +100%)"
                        />
                    </div>
                    <div className={styles.overrideFormActions}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSave}
                            disabled={isSaving || !formData.sku}
                        >
                            {isSaving ? <CircularProgress size={16} /> : 'Save Override'}
                        </Button>
                        <Button size="small" onClick={resetForm} disabled={isSaving}>
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            <ConfirmDeleteOverrideDialog />
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Billing History Section
// ──────────────────────────────────────────────────────────────────────

function BillingHistorySection({ billingPeriods }) {
    if (!billingPeriods || billingPeriods.length === 0) {
        return (
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitleIcon}>
                        <ReceiptLongIcon color="action" />
                        <h3 className={styles.sectionTitle}>
                            Billing History (All Cases)
                        </h3>
                    </div>
                </div>
                <p className={styles.emptyText}>
                    No billing history yet.
                </p>
            </div>
        );
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div className={styles.sectionTitleIcon}>
                    <ReceiptLongIcon color="action" />
                    <h3 className={styles.sectionTitle}>
                        Billing History (All Cases)
                    </h3>
                </div>
            </div>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Period</TableCell>
                            <TableCell>Case</TableCell>
                            <TableCell>Invoice #</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {billingPeriods.map((bp, idx) => {
                            const statusConfig = PERIOD_STATUS[bp.status] || { label: bp.status, color: 'default' };
                            return (
                                <TableRow key={idx}>
                                    <TableCell>{bp.period}</TableCell>
                                    <TableCell>{bp.caseName || bp.caseID}</TableCell>
                                    <TableCell>
                                        <span className={styles.fieldValueMono}>
                                            {bp.invoiceNumber || '\u2014'}
                                        </span>
                                    </TableCell>
                                    <TableCell align="right">
                                        {bp.invoiceTotal != null ? `$${bp.invoiceTotal.toFixed(2)}` : '\u2014'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={statusConfig.label}
                                            color={statusConfig.color}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Account Details Section
// ──────────────────────────────────────────────────────────────────────

function AccountDetailsSection({ customer, customerID, onRefresh }) {
    const snackbarContext = useContext(SnackbarContext);
    const [editing, setEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const isOrg = !!customer?.orgName;

    const [formData, setFormData] = useState({
        orgName: customer?.orgName || '',
        orgEmail: customer?.orgEmail || '',
        orgPhone: customer?.orgPhone || '',
        phone: customer?.phone || '',
    });

    // Reset form when customer changes
    React.useEffect(() => {
        setFormData({
            orgName: customer?.orgName || '',
            orgEmail: customer?.orgEmail || '',
            orgPhone: customer?.orgPhone || '',
            phone: customer?.phone || '',
        });
        setEditing(false);
    }, [customer]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            const payload = { customerID };
            if (isOrg) {
                payload.orgName = formData.orgName.trim();
                payload.orgEmail = formData.orgEmail.trim();
                payload.orgPhone = formData.orgPhone.trim();
            } else {
                payload.phone = formData.phone.trim();
            }
            await updateCustomerDetails(payload);
            snackbarContext.openSnackbarSuccess('Account details updated');
            setEditing(false);

            // Optimistically update the cache with new values
            mutate(
                ['BILLING_SETTINGS', customerID],
                (current) => {
                    if (!current) return current;
                    return {
                        ...current,
                        customer: {
                            ...current.customer,
                            ...payload,
                        },
                    };
                },
                { revalidate: true },
            );
        } catch (error) {
            console.error('Error updating details:', error);
            snackbarContext.openSnackbarError('Failed to update account details');
        } finally {
            setIsSaving(false);
        }
    }, [customerID, formData, isOrg, snackbarContext]);

    const handleCancel = () => {
        setFormData({
            orgName: customer?.orgName || '',
            orgEmail: customer?.orgEmail || '',
            orgPhone: customer?.orgPhone || '',
            phone: customer?.phone || '',
        });
        setEditing(false);
    };

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                    Account Details
                </h3>
                {!editing && (
                    <IconButton size="small" onClick={() => setEditing(true)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
            </div>

            {isOrg ? (
                // Organization fields
                <div className={styles.fieldGroup}>
                    {editing ? (
                        <>
                            <TextField
                                label="Organization Name"
                                value={formData.orgName}
                                onChange={(e) => setFormData(prev => ({ ...prev, orgName: e.target.value }))}
                                fullWidth
                                required
                                size="small"
                                disabled={isSaving}
                            />
                            <TextField
                                label="Billing Email"
                                value={formData.orgEmail}
                                onChange={(e) => setFormData(prev => ({ ...prev, orgEmail: e.target.value }))}
                                fullWidth
                                size="small"
                                disabled={isSaving}
                                helperText="Invoices and billing notifications are sent to this email"
                            />
                            <TextField
                                label="Phone"
                                value={formData.orgPhone}
                                onChange={(e) => setFormData(prev => ({ ...prev, orgPhone: e.target.value }))}
                                fullWidth
                                size="small"
                                disabled={isSaving}
                            />
                        </>
                    ) : (
                        <>
                            <div>
                                <p className={styles.fieldLabel}>Organization Name</p>
                                <p className={styles.fieldValue}>{customer?.orgName || '\u2014'}</p>
                            </div>
                            <div>
                                <p className={styles.fieldLabel}>Billing Email</p>
                                <p className={styles.fieldValue}>{customer?.orgEmail || '\u2014'}</p>
                            </div>
                            <div>
                                <p className={styles.fieldLabel}>Phone</p>
                                <p className={styles.fieldValue}>{customer?.orgPhone || '\u2014'}</p>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                // User customer fields
                <div className={styles.fieldGroup}>
                    <div>
                        <p className={styles.fieldLabel}>Name</p>
                        <p className={styles.fieldValue}>
                            {customer?.firstName} {customer?.lastName}
                        </p>
                    </div>
                    <div>
                        <p className={styles.fieldLabel}>Email</p>
                        <p className={styles.fieldValue}>{customer?.email || '\u2014'}</p>
                    </div>
                    {editing ? (
                        <TextField
                            label="Phone"
                            value={formData.phone}
                            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                            fullWidth
                            size="small"
                            disabled={isSaving}
                        />
                    ) : (
                        <div>
                            <p className={styles.fieldLabel}>Phone</p>
                            <p className={styles.fieldValue}>{customer?.phone || '\u2014'}</p>
                        </div>
                    )}
                </div>
            )}

            {editing && (
                <div className={styles.editActions}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                    <Button size="small" onClick={handleCancel} disabled={isSaving}>
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Current Plan Summary
// ──────────────────────────────────────────────────────────────────────

function PlanSummary({ billingStrategy, paymentMethodStatus }) {
    const hasPayment = paymentMethodStatus === 'active';

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle} style={{ marginBottom: 'var(--space-4)' }}>
                Current Plan
            </h3>
            <div className={styles.planRow}>
                <div className={styles.planInfo}>
                    <p className={styles.planName}>
                        {getStrategyLabel(billingStrategy)}
                    </p>
                    <p className={styles.planDesc}>
                        {getStrategyDescription(billingStrategy)}
                    </p>
                </div>
                <Divider orientation="vertical" flexItem />
                <div className={styles.planPaymentStatus}>
                    {hasPayment ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                        <WarningIcon color="warning" fontSize="small" />
                    )}
                    <p className={styles.planPaymentText}>
                        {hasPayment ? 'Payment method on file' : 'No active payment method'}
                    </p>
                </div>
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────
// Billing Actions Section (super admin only)
// ──────────────────────────────────────────────────────────────────────

function BillingActionsSection({ caseID, caseName, snackbarContext, onRefresh, hasPaymentMethod }) {
    const [isCharging, setIsCharging] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);

    const handleMidperiodCharge = useCallback(async () => {
        setIsCharging(true);
        try {
            await adminMidperiodCharge(caseID);
            snackbarContext.openSnackbarSuccess(
                'Mid-period charge triggered \u2014 check billing history shortly'
            );
            setTimeout(() => onRefresh(), 5000);
        } catch (error) {
            snackbarContext.openSnackbarError('Failed to trigger mid-period charge');
        } finally {
            setIsCharging(false);
        }
    }, [caseID, snackbarContext, onRefresh]);

    const handleFinalize = useCallback(async () => {
        setIsFinalizing(true);
        try {
            await adminFinalizeInvoice(caseID);
            snackbarContext.openSnackbarSuccess(
                'Period finalization triggered \u2014 check billing history shortly'
            );
            setTimeout(() => onRefresh(), 5000);
        } catch (error) {
            snackbarContext.openSnackbarError('Failed to trigger finalization');
        } finally {
            setIsFinalizing(false);
        }
    }, [caseID, snackbarContext, onRefresh]);

    const { handleConfirmAction: confirmCharge, ConfirmDialogElement: ConfirmChargeDialog } =
        useConfirmAction(handleMidperiodCharge, {
            title: 'Mid-Period Charge',
            confirmMessage: `Charge the current open invoice balance for "${caseName || caseID}"? This will immediately charge this case's payment method (or the customer default).`,
        });

    const { handleConfirmAction: confirmFinalize, ConfirmDialogElement: ConfirmFinalizeDialog } =
        useConfirmAction(handleFinalize, {
            title: 'Finalize Past Periods',
            confirmMessage: `Close any open billing periods from prior months for "${caseName || caseID}"? This will add scheduled fees, send invoices, and attempt charges for any unfulfilled past periods.`,
        });

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                    Billing Actions
                </h3>
                <Chip label="Super Admin" size="small" color="error" variant="outlined" />
            </div>

            <div className={styles.actionsRow}>
                {hasPaymentMethod ? (
                    <div className={styles.actionCol}>
                        <Button
                            variant="outlined"
                            color="warning"
                            fullWidth
                            onClick={() => confirmCharge()}
                            disabled={isCharging || isFinalizing}
                            startIcon={isCharging ? <CircularProgress size={16} /> : null}
                        >
                            {isCharging ? 'Charging...' : 'Charge Current Balance'}
                        </Button>
                        <span className={styles.actionDesc}>
                            Charges the open invoice balance without closing the period
                        </span>
                    </div>
                ) : (
                    <div className={styles.actionCol}>
                        <Button variant="outlined" fullWidth disabled>
                            Charge Current Balance
                        </Button>
                        <span className={styles.actionDescWarning}>
                            No payment method on file \u2014 customer must add one before charging
                        </span>
                    </div>
                )}
                <div className={styles.actionCol}>
                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={() => confirmFinalize()}
                        disabled={isCharging || isFinalizing}
                        startIcon={isFinalizing ? <CircularProgress size={16} /> : null}
                    >
                        {isFinalizing ? 'Finalizing...' : 'Finalize Past Periods'}
                    </Button>
                    <span className={styles.actionDesc}>
                        Closes any open billing periods from prior months
                    </span>
                </div>
            </div>

            <ConfirmChargeDialog />
            <ConfirmFinalizeDialog />
        </div>
    );
}


// ──────────────────────────────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────────────────────────────

export default function BillingSettingsPage() {
    const { userProfile, isLoading: isProfileLoading } = useContext(UserProfileContext);
    const snackbarContext = useContext(SnackbarContext);

    const customerID = userProfile?.currentCustomer?.customerID;
    const isSuperAdmin = userProfile?.isSuperAdmin;

    const currentCaseID = userProfile?.currentCase?.caseID || null;
    const { data: settings, error: fetchError, isLoading, mutate: refreshSettings } = useGetBillingSettings(
        customerID, !!customerID, currentCaseID
    );

    const handleRefresh = useCallback(() => {
        return refreshSettings();
    }, [refreshSettings]);

    // Guard: no customer
    if (!customerID) {
        return (
            <div className={styles.guardContainer}>
                <Alert severity="info">
                    Please select a Customer from the context menu to view billing settings.
                </Alert>
            </div>
        );
    }

    // Guard: not customer admin
    if (userProfile && !userProfile.isCustomerAdmin()) {
        return (
            <div className={styles.guardContainer}>
                <Alert severity="warning">
                    You do not have permission to view billing settings for this Customer.
                </Alert>
            </div>
        );
    }

    // Loading
    if (isLoading || isProfileLoading) {
        return (
            <div className={styles.loadingContainer}>
                <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={120} sx={{ mb: 3, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={200} sx={{ mb: 3, borderRadius: 1 }} />
            </div>
        );
    }

    // Error
    if (fetchError) {
        return (
            <div className={styles.guardContainer}>
                <Alert severity="error">
                    Failed to load billing settings. Please try refreshing the page.
                </Alert>
            </div>
        );
    }

    if (!settings) return null;

    const customerDisplayName = userProfile?.currentCustomer?.orgName
        || `${userProfile?.currentCustomer?.firstName || ''} ${userProfile?.currentCustomer?.lastName || ''}`.trim()
        || customerID;

    return (
        <div className={styles.page}>
            {/* Header */}
            <PageHeader
                title="Billing & Subscription"
                subtitle={customerDisplayName}
            />

            {/* Current Plan */}
            <PlanSummary
                billingStrategy={settings.billingStrategy}
                paymentMethodStatus={settings.paymentMethodStatus}
            />

            {/* Account Details */}
            <AccountDetailsSection
                customer={settings.customer}
                customerID={customerID}
                onRefresh={handleRefresh}
            />

            {/* Billing Strategy (super admin) */}
            {isSuperAdmin && (
                <BillingStrategySection
                    currentStrategy={settings.billingStrategy}
                    customerID={customerID}
                    onRefresh={handleRefresh}
                />
            )}

            {/* Payment Methods */}
            <PaymentMethodsSection
                paymentMethods={settings.paymentMethods || []}
                customerID={customerID}
                onRefresh={handleRefresh}
            />
            <span className={styles.disclosureText}>
                Payments are processed by Intuit Payments Inc., 2700 Coast Avenue, Mountain View, CA 94043, Phone 1-888-536-4801, NMLS #1098819
            </span>

            {/* Pricing Overrides (super admin) */}
            {isSuperAdmin && (
                <PricingOverridesSection
                    overrides={settings.overrides || []}
                    customerID={customerID}
                    onRefresh={handleRefresh}
                />
            )}

            {/* Per-case payment method (case selected, customer has stored methods) */}
            {userProfile?.currentCase?.caseID && settings.paymentMethods?.length > 0 && (
                <CasePaymentMethodSection
                    caseID={userProfile.currentCase.caseID}
                    caseName={userProfile.currentCase.caseName}
                    customerID={customerID}
                    paymentMethods={settings.paymentMethods}
                    casePaymentMethod={settings.casePaymentMethod || null}
                    onRefresh={handleRefresh}
                />
            )}

            {/* Billing Usage for Current Period */}
            {userProfile?.currentCase?.caseID && (
                <CaseUsageSummary
                    caseID={userProfile.currentCase.caseID}
                    caseName={userProfile.currentCase.caseName}
                />
            )}

            {/* Billing Actions (super admin only, case selected) */}
            {isSuperAdmin && userProfile?.currentCase?.caseID && (
                <BillingActionsSection
                    caseID={userProfile.currentCase.caseID}
                    caseName={userProfile.currentCase.caseName}
                    snackbarContext={snackbarContext}
                    onRefresh={handleRefresh}
                    hasPaymentMethod={settings.paymentMethods?.length > 0}
                />
            )}

            {/* Billing History */}
            <BillingHistorySection
                billingPeriods={settings.billingPeriods || []}
            />
        </div>
    );
}
